import { Store } from "vuex";
import getCacheKey from "@/vue/planning/logic/getCacheKey";
import responsesCache from "@/vue/planning/state/responsesCache";
import { getAvailRequestData } from "@/vue/planning/logic/getRequestData";
import { AvailabilitiesResponse, AvailablePackage, PrestationRaw } from "@/types/responses";
import * as _ from "lodash";
import { getUniqueId } from "@/vue/planning/logic/uniqueId";
import isHaveAvailability from "@/vue/planning/logic/isHaveAvailability";
import { checkIsSummerOrWinter } from "@/utils/utils";
import { PrestationData } from "@/types";
import { getAvailablePackagesWiyp } from "./availablesPackagesWiyp";
import flightPackCode from "@/config/flightPackCode";
import transportPackCode from "@/config/transportPackCode";
import { trainStationCodeMap } from "@/config/trainStationCodeMap";
import { airportCodeMap } from "@/config/airportCodeMap";
import { getAllowedOriginsForTrainPack, getNativeOriginsForTrainPack, findTrainPrestIdForOrigin, getTrainPackageFamily } from "@/config/trainPackageConfig";
import Cookies from "js-cookie";

const syncPackAvailabilitiesToStore = (
  availResp: AvailabilitiesResponse,
  packCode: string,
  storeObj: Store<any>
): AvailabilitiesResponse => {
  // 1️⃣  Ensure every prestation has a deterministic ID
  if (!availResp.prestations)
    throw new Error("prestations are required in availResp.prestations");

  availResp.prestations = availResp.prestations.map((prest: any) => {
    prest["id"] = getUniqueId(prest);
    return prest;
  });

  // 2️⃣  Build a `{ [id]: prest }` map for the given packCode and its family
  const prestById: Record<string, any> = {};
  availResp.prestations.forEach(
    (prest: any) => (prestById[prest.id] = prest)
  );

  const newMapObj: Record<string, any> = {
    ...storeObj.state.prestMaps.seAvailabilities,
  };
  getTrainPackageFamily(packCode).forEach((familyPackCode) => {
    newMapObj[familyPackCode] = prestById;
  });

  // 3️⃣  Sync both maps in the store
  storeObj.state.prestMaps.seAvailabilities = newMapObj;
  storeObj.state.prestMaps.prices = newMapObj;

  // Here we must adjust wiyp data with this
  // ex. newMapObj:  codepackage PFTT - prestId 800371 - prestIdFusion/prestIdSource 185330
  // Iterate through newMapObj (which has one key: codePackage, e.g., 'PFTT')
  // On the last level we have objects like {id: ..., prestId, prestIdFusion, ...}
  // Ex. ['PFTT-874568-185330-185330', 'PFTT-800371-185330-185330', 'PFTT-800371-812705-812705', 'PFTT-800371-185331-185331']
  // Create a map with codePackage as key and array of prestation property objects as value
  const packagePrestMap = new Map<string, Array<{ prestId: string, prestIdSource: string, prestIdFusion: string }>>();

  availResp.prestations.forEach((prest: any) => {
    if (!packagePrestMap.has(prest.codePackage)) {
      packagePrestMap.set(prest.codePackage, []);
    }
    packagePrestMap.get(prest.codePackage)!.push({
      prestId: prest.prestId,
      prestIdSource: prest.prestIdSource,
      prestIdFusion: prest.prestIdFusion
    });
  });

  // Object.entries(newMapObj).forEach(([codePackage, level2]) => {
  //   packagePrestMap.set(codePackage, Object.values(level2).map((prest: any) => ({
  //     prestId: prest.prestId,
  //     prestIdSource: prest.prestIdSource,
  //     prestIdFusion: prest.prestIdFusion
  //   })));
  // });
  storeObj.dispatch("updateWiypCallWithAvailability", packagePrestMap);


  return availResp;
};

/**
 * The `prestations` getter matches an availability to its catalog data through
 * `prestIdSource`, so that is the id we must fetch and the id we must look for
 * in the store. `prestId` points to the package prestation, which the catalog
 * only resolves for HS.
 */
const updateDisplayedPrestations = async (storeObj: Store<any>, availabilities: AvailabilitiesResponse) => {
    const knownPrestationIds = new Set(
      (storeObj.state.initialPrestationsArray || []).map((prest: PrestationData) => String(prest?.prestationId))
    );

    const missingPrestIdSources = [
      ...new Set(
        (availabilities.prestations || [])
          .map((prest) => prest.prestIdSource)
          .filter((prestIdSource): prestIdSource is string => !!prestIdSource && !knownPrestationIds.has(String(prestIdSource)))
      ),
    ];

    if(missingPrestIdSources.length === 0) return

    const prestFromCatalogPromises = missingPrestIdSources.map(prestIdSource => {
      const requestData = {
        locale: storeObj.getters.locale,
        prestationId: prestIdSource,
      };
      return window.catalogService.getPrestationByLocale(requestData).catch(() => null);
    });
    
    const results = await Promise.all(prestFromCatalogPromises);

    // The catalog answers `{ message: { prestation: null } }` for unknown ids;
    // keeping those would pollute `initialPrestationsArray` with unusable entries.
    const prestationsFromCatalog = results.filter((prest: any) => prest?.prestationId);

    if(prestationsFromCatalog.length === 0) return
    
    storeObj.dispatch("updateInitialPrestations", prestationsFromCatalog)
}

/**
 * Filter out available packages and prestations that have bad combinations of codePackage and origins
 * Example: a flight package with a train origin will be filtered out
 * @param avail - Array of available packages or prestations
 * @returns Array of available packages or prestations that have valid combinations of codePackage and origins
 */
const filterAvailablePackagesForBadCombinations = (avail: Array<AvailablePackage> | Array<PrestationRaw>, storeObj: Store<any>) => {
  if (avail.length > 0) {
    const filtered = avail.filter((packObj: any) => {

      // First we filter out CSE packages for NL & FR to avoid PFTTF to NL or PFTTNL to FR
      if (storeObj.state.language === "fr-FR-CSE") {
        const lang = Cookies.get('companyLang') ?? 'fr'
        if (lang === 'nl' && ['PFTTF', 'PFPTTF', 'PFPTT'].includes(packObj.codePackage)) {
          return false;
        }
        else if (['PFTTNL', 'PFPTTNL'].includes(packObj.codePackage)) {
          return false;
        }
      }
      
      const isFlightPackage = flightPackCode.includes(packObj.codePackage);
      const isTrainPackage = transportPackCode.includes(packObj.codePackage);
      const possibleOrigins: string[] | undefined = packObj.origins;

      if (isFlightPackage || isTrainPackage) {
        
        // Intersect possibleOrigins with respective code map depending on transport type
        if (isFlightPackage) {
          // If no origins are provided for a flight package, filter it out
          if (!possibleOrigins || !Array.isArray(possibleOrigins) || possibleOrigins.length === 0) {
            return false;
          }
          packObj.origins = possibleOrigins.filter(origin => airportCodeMap.has(origin));
          if (!packObj.origins || packObj.origins.length === 0) {
            return false; // No valid flight origins
          }
        }

        // Special logic for train : allow if no origins are provided
        if (isTrainPackage) {
          const nativeOrigins = getNativeOriginsForTrainPack(packObj.codePackage);
          const allowedOrigins = nativeOrigins ?? getAllowedOriginsForTrainPack(packObj.codePackage);
          if (!possibleOrigins || !Array.isArray(possibleOrigins) || possibleOrigins.length === 0) {
            if (allowedOrigins) {
              packObj.origins = [...allowedOrigins];
            }
            return true;
          }
          packObj.origins = possibleOrigins.filter(origin => {
            if (!trainStationCodeMap.has(origin)) return false;
            if (allowedOrigins && !allowedOrigins.includes(origin)) return false;
            return true;
          });
          if (!packObj.origins || packObj.origins.length === 0) {
            return false; // No valid train origins if origins are provided
          }
        }
      }
      return true;
    });
    return filtered;
  }
  return avail;
}

const getAvailability = async (
  packCode: string,
  startDate: string,
  endDate: string,
  storeObj: Store<any>
): Promise<AvailabilitiesResponse | null> => {
  if (storeObj.state.datepickerStartDate) {
    const newSeason = checkIsSummerOrWinter(storeObj.state.datepickerStartDate);
    if (newSeason !== storeObj.state.season) {
      storeObj.state.season = newSeason;

      window.seasonManager.switchSeasonWithoutReloading();
    }
  }
  // try to extract cached response
  const cacheKey = getCacheKey(
    packCode,
    startDate,
    endDate,
    storeObj.state.season,
    storeObj.state.participants,
    storeObj.state.skipass
  );

  const existingAvailabilities = responsesCache.value.getResponse(
    "availabilities",
    cacheKey
  ) as AvailabilitiesResponse;
  
  if(existingAvailabilities && existingAvailabilities.prestations) {
    storeObj.state.availablePackageCodeWiyp = getAvailablePackagesWiyp(existingAvailabilities);
    resolveAndSetPackageId(storeObj, packCode, existingAvailabilities);
    await updateDisplayedPrestations(storeObj, existingAvailabilities)
    return syncPackAvailabilitiesToStore(existingAvailabilities, packCode, storeObj);
  }

  const requestData = getAvailRequestData(
    storeObj.state,
    storeObj.getters,
    packCode
  );

  const avail_resp: AvailabilitiesResponse =
    await window.searchEngineService.getRelatedPackages(
      requestData,
      "getAvailabilities"
    );
  // We filter all bad codepackages/origin combinations
  avail_resp.availablePackages = filterAvailablePackagesForBadCombinations(avail_resp.availablePackages || [], storeObj);
  avail_resp.prestations = filterAvailablePackagesForBadCombinations(avail_resp.prestations || [], storeObj);
  storeObj.state.availablePackageCodeWiyp = getAvailablePackagesWiyp(avail_resp);

  if (!isHaveAvailability(avail_resp)) {
    return null;
  }

  resolveAndSetPackageId(storeObj, packCode, avail_resp);

  if (!avail_resp.prestations)
    throw new Error(
      "No prestations was received from the `availabilities` endpoint"
    );

  // Fusion products live on another lihe and are absent from the page catalog —
  // fetch their catalog data so they can be matched in the prestations getter.
  await updateDisplayedPrestations(storeObj, avail_resp);

  syncPackAvailabilitiesToStore(avail_resp, packCode, storeObj);
  responsesCache.value.setResponse("availabilities", cacheKey, avail_resp);
  getTrainPackageFamily(packCode).forEach((familyPackCode) => {
    if (familyPackCode === packCode) return;
    responsesCache.value.setResponse(
      "availabilities",
      getCacheKey(
        familyPackCode,
        startDate,
        endDate,
        storeObj.state.season,
        storeObj.state.participants,
        storeObj.state.skipass
      ),
      avail_resp
    );
  });
  responsesCache.value = _.cloneDeep(responsesCache.value);

  return avail_resp;
};

/**
 * For HS, `availablePackages[].prestId` can be a fusion accommodation id
 * (e.g. "718542") rather than `${liheId}HS`. Always use the page lihe for HS.
 */
const resolveAndSetPackageId = (
  storeObj: Store<any>,
  packCode: string,
  availResp: AvailabilitiesResponse
) => {
  if (packCode === "HS") {
    storeObj.commit("setPackageId", `${storeObj.state.liheData.id}HS`);
    return;
  }

  const origin = storeObj.state.flightOrigin?.split("-")[0];
  const originPrestId = findTrainPrestIdForOrigin(
    [...(availResp.prestations || []), ...(availResp.availablePackages || [])],
    packCode,
    origin
  );

  const packageId =
    originPrestId ||
    availResp?.availablePackages?.find((packObj: any) => {
      return packObj.codePackage === packCode;
    })?.prestId;

  storeObj.commit("setPackageId", packageId);
};

export default getAvailability;
